import { useEffect, useState, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { motion } from "framer-motion";

const CORRIDOR_IMG = "https://customer-assets.emergentagent.com/job_medboard-exec/artifacts/ni62ugk2_Imagen%20pasillo%20hospital.png";

const OBSERVER_OPTIONS = { threshold: 0.5 };
const ANIM_DURATION = 2000;

function AnimatedCounter({ target, suffix = "%" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let startTime = 0;
        const step = (timestamp) => {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / ANIM_DURATION, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.round(eased * target));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, OBSERVER_OPTIONS);

    observer.observe(node);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="font-playfair text-6xl md:text-7xl text-[#1a6cf5]">
      {count}{suffix}
    </span>
  );
}

const metricInitial = { opacity: 0, y: 30 };
const metricAnimate = { opacity: 1, y: 0 };
const onceViewport = { once: true };

export default function Metrics() {
  const { t } = useLanguage();
  const items = t("metrics.items");

  if (!Array.isArray(items)) return null;

  return (
    <section data-testid="metrics-section" className="relative py-16 md:py-20 bg-[#0a0f1f]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {items.map((item, i) => (
            <motion.div
              key={item.label}
              data-testid={`metric-${i}`}
              initial={metricInitial}
              whileInView={metricAnimate}
              viewport={onceViewport}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="text-center"
            >
              <AnimatedCounter target={item.value} />
              <p className="font-outfit text-sm text-[#94a3b8] mt-4 leading-relaxed">{item.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
